import { Option } from "./option.model";
import { OPTION_LABEL_DEFAULTS } from "./option-label-defaults";

export class OptionUtils {

  /**
   * Create an Option tagged with a defaults-lookup key and pre-populated
   * `_i18n` translations drawn from OPTION_LABEL_DEFAULTS.
   *
   * @param defaultsKey compound path into OPTION_LABEL_DEFAULTS (e.g. 'assets.digital')
   */
  static create(
    defaultsKey: string,
    name: string,
    text: string,
    explanation: string,
    value: any,
    iconName?: string,
    iconClass?: string,
  ): Option {
    const option = new Option(name, text, explanation, value, iconName, iconClass);
    option._defaultsKey = defaultsKey;
    const translations = OPTION_LABEL_DEFAULTS[defaultsKey];
    if (translations) {
      option._i18n = {};
      for (const [lang, labels] of Object.entries(translations)) {
        option._i18n[lang] = { ...(labels as Record<string, string>) };
      }
    }
    return option;
  }
}

/**
 * Three-tier fallback resolver for Option.text / Option.explanation.
 *
 * 1. Admin `_i18n` override — `option._i18n[lang][field]`
 * 2. Built-in default — `OPTION_LABEL_DEFAULTS[option._defaultsKey][lang][field]`
 * 3. English base — `option[field]`
 */
export function getOptionLabel(
  option: Option | undefined | null,
  field: 'text' | 'explanation',
  lang: string | undefined | null,
): string {
  if (!option) {
    return '';
  }
  if (lang) {
    // Tier 1: admin override
    const adminOverride = option._i18n?.[lang]?.[field];
    if (adminOverride) {
      return adminOverride;
    }
    // Tier 2: built-in default
    if (option._defaultsKey) {
      const builtIn = OPTION_LABEL_DEFAULTS[option._defaultsKey]?.[lang]?.[field];
      if (builtIn) {
        return builtIn;
      }
    }
  }
  // Tier 3: English base
  return option[field] ?? '';
}
